import { useState, type FormEvent } from 'react';
import { color } from '../styles/theme';
import { useChangePassword, passwordProblems, problemsFrom } from '../api/account';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Button, inputStyle } from './ui';
import { IconSpinner } from './icons';
import { AuthShell, PasswordChecklist } from './AuthShell';

/**
 * The first sign-in after an invite (API migration 049).
 *
 * The invite emailed a temporary password; this asks for the person's own
 * before anything else opens. It stands in for the dashboard rather than
 * floating over it, because the API refuses every other request until a
 * password is chosen. The temporary password is the "current" one: carried
 * over from the sign-in form when we have it, asked for after a reload.
 */
export function ChooseFirstPassword() {
  const { admin, temporaryPassword, refresh, signOut } = useAuth();
  const change = useChangePassword();
  const [temporary, setTemporary] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const current = temporaryPassword ?? temporary;
  const local = passwordProblems(password);
  const serverProblems = problemsFrom(change.error);
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready =
    current.length > 0 && password.length > 0 && local.length === 0 && !mismatch && confirm.length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: password });
      // The session carries on; re-reading it lifts the restriction.
      await refresh();
    } catch (err) {
      const problems = problemsFrom(err);
      setError(
        problems.length > 0
          ? problems.join('. ')
          : err instanceof ApiError
            ? err.message
            : 'Could not save your password. Try again.',
      );
    }
  }

  const first = admin?.name.trim().split(/\s+/)[0];

  return (
    <AuthShell
      title="ENACT Lab"
      sub="Choose your password"
      onSubmit={submit}
      footnote={
        <>
          Not you?{' '}
          <button
            type="button"
            onClick={() => void signOut()}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: color.blue,
              fontWeight: 700,
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            Sign out
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, color: color.ink, margin: 0, lineHeight: 1.6 }}>
        {first ? `Welcome, ${first}. ` : 'Welcome. '}You signed in with a temporary password. Choose
        your own to continue — the temporary one stops working once you do.
      </p>

      {temporaryPassword === null && (
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>
            Temporary password, from your invitation
          </span>
          <input
            type="password"
            value={temporary}
            autoComplete="current-password"
            required
            onChange={(e) => setTemporary(e.target.value)}
            style={inputStyle}
          />
        </label>
      )}

      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>New password</span>
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          required
          autoFocus
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
      </label>

      <PasswordChecklist
        problems={password.length > 0 ? local : serverProblems}
        dirty={password.length > 0}
      />

      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>Confirm password</span>
        <input
          type="password"
          value={confirm}
          autoComplete="new-password"
          required
          onChange={(e) => setConfirm(e.target.value)}
          style={inputStyle}
        />
      </label>

      {mismatch && (
        <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>
          Those two passwords do not match.
        </p>
      )}
      {error && <p style={{ color: color.red, fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>}

      <Button type="submit" disabled={!ready || change.isPending} style={{ justifyContent: 'center', marginTop: 4 }}>
        {change.isPending && <IconSpinner size={15} />}
        {change.isPending ? 'Saving…' : 'Save and continue'}
      </Button>
    </AuthShell>
  );
}
